import { createElement, forwardRef } from 'react';
import MuiGrid from '@mui/material/Grid';
import type { GridProps as MuiGridProps } from '@mui/material/Grid';

type GridSize = 'auto' | 'grow' | number | false;

// Legacy breakpoint props from MUI v5/v6
interface LegacyGridProps {
  item?: boolean;
  xs?: GridSize | boolean;
  sm?: GridSize | boolean;
  md?: GridSize | boolean;
  lg?: GridSize | boolean;
  xl?: GridSize | boolean;
  size?: Record<string, GridSize> | GridSize;
}

export type GridProps = MuiGridProps & LegacyGridProps;

const BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

/**
 * Compatibility wrapper for MUI Grid.
 *
 * MUI v7 replaced breakpoint props (xs, sm, md, lg, xl) with a single `size` prop
 * and removed the `item` prop. This wrapper maps the v5/v6 API so existing code
 * continues to work.
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>(function Grid(props, ref) {
  const { item: _item, xs, sm, md, lg, xl, size, ...rest } = props;

  let resolvedSize = size;

  if (resolvedSize === undefined) {
    const breakpointValues = { xs, sm, md, lg, xl };
    const bp: Record<string, GridSize> = {};

    for (const key of BREAKPOINTS) {
      const val = breakpointValues[key];
      if (val !== undefined) {
        bp[key] = val === true ? 'grow' : (val as GridSize);
      }
    }

    if (Object.keys(bp).length > 0) {
      resolvedSize = bp;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createElement(MuiGrid, { ref, size: resolvedSize, ...rest } as any);
});

export type { MuiGridProps };
